import React, { useLayoutEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import type { Game, Round } from "./types";
import styles from "./Resultats.module.css";

interface ConnectorLine {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  midX: number;
  highlighted: boolean;
}

interface BracketConnectorsProps {
  rounds: Round[];
  connected: boolean;
  pathMatchIds: Set<string>;
  /** Re-mesure quand la sélection change le rendu. */
  layoutKey?: string;
}

function escapeAttr(value: string): string {
  return value.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
}

function matchCenterY(
  container: HTMLElement,
  matchId: string
): number | null {
  const el = container.querySelector(
    `[data-match-id="${escapeAttr(matchId)}"]`
  );
  if (!(el instanceof HTMLElement)) return null;
  const cRect = container.getBoundingClientRect();
  const mRect = el.getBoundingClientRect();
  return mRect.top - cRect.top + mRect.height / 2;
}

function matchRightX(
  container: HTMLElement,
  matchId: string
): number | null {
  const el = container.querySelector(
    `[data-match-id="${escapeAttr(matchId)}"]`
  );
  if (!(el instanceof HTMLElement)) return null;
  const cRect = container.getBoundingClientRect();
  const mRect = el.getBoundingClientRect();
  return mRect.right - cRect.left;
}

function matchLeftX(
  container: HTMLElement,
  matchId: string
): number | null {
  const el = container.querySelector(
    `[data-match-id="${escapeAttr(matchId)}"]`
  );
  if (!(el instanceof HTMLElement)) return null;
  const cRect = container.getBoundingClientRect();
  const mRect = el.getBoundingClientRect();
  return mRect.left - cRect.left;
}

function collectLinkedGames(rounds: Round[]): Game[] {
  return rounds.flatMap((r) => r.matches).filter((m) => m.nextMatchId);
}

export default function BracketConnectors({
  rounds,
  connected,
  pathMatchIds,
  layoutKey = "",
}: BracketConnectorsProps): React.ReactElement | null {
  const theme = useTheme();
  const overlayRef = useRef<HTMLDivElement>(null);
  const [lines, setLines] = useState<ConnectorLine[]>([]);
  const [size, setSize] = useState({ w: 0, h: 0 });

  useLayoutEffect(() => {
    if (!connected) {
      setLines([]);
      return;
    }
    const overlay = overlayRef.current;
    const container = overlay?.parentElement;
    if (!container) return;

    const measure = (): void => {
      const rect = container.getBoundingClientRect();
      setSize({ w: rect.width, h: rect.height });
      const next: ConnectorLine[] = [];
      for (const game of collectLinkedGames(rounds)) {
        if (!game.nextMatchId) continue;
        const y1 = matchCenterY(container, game.id);
        const y2 = matchCenterY(container, game.nextMatchId);
        const x1 = matchRightX(container, game.id);
        const x2 = matchLeftX(container, game.nextMatchId);
        if (y1 == null || y2 == null || x1 == null || x2 == null) continue;
        const midX = x1 + (x2 - x1) / 2;
        next.push({
          x1,
          y1,
          x2,
          y2,
          midX,
          highlighted:
            pathMatchIds.has(game.id) && pathMatchIds.has(game.nextMatchId),
        });
      }
      setLines(next);
    };

    measure();
    const ro = new ResizeObserver(() => measure());
    ro.observe(container);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [rounds, connected, pathMatchIds, layoutKey]);

  if (!connected) return null;

  const stroke = alpha(
    theme.palette.mode === "dark"
      ? theme.palette.common.white
      : theme.palette.primary.main,
    theme.palette.mode === "dark" ? 0.45 : 0.4
  );
  const strokeHi = theme.palette.secondary.main;

  return (
    <Box
      ref={overlayRef}
      className={styles.bracketConnectors}
      aria-hidden
      sx={{ pointerEvents: "none" }}
    >
      {size.w > 0 && size.h > 0 && (
        <svg width={size.w} height={size.h} className={styles.bracketSvg}>
          {lines.map((line, index) => (
            <path
              key={`${line.x1}-${line.y1}-${index}`}
              d={`M ${line.x1} ${line.y1} H ${line.midX} V ${line.y2} H ${line.x2}`}
              fill="none"
              stroke={line.highlighted ? strokeHi : stroke}
              strokeWidth={line.highlighted ? 2.5 : 1.5}
              strokeLinecap="round"
              strokeLinejoin="round"
              opacity={line.highlighted ? 1 : 0.85}
            />
          ))}
        </svg>
      )}
    </Box>
  );
}
